import { uploadErrorMessage } from "@welearn/upload-client";
import { uploads } from "./uploads";
import { httpClient } from "./httpClient";
import type { PageResult } from "./adminTypes";
export type BannerInput = {
  title: string;
  subtitle: string;
  client: "h5" | "pc" | "both";
  h5_image_id: string;
  pc_image_id: string;
  target_type: "course" | "category" | "courses" | "none";
  target_id: string;
  sort_order: number;
  starts_at: string | null;
  ends_at: string | null;
  version: number;
};
export type Banner = BannerInput & {
  id: string;
  status: "draft" | "enabled" | "disabled";
  display_status: string;
  invalid_reason: string;
  h5_rank: number;
  pc_rank: number;
  created_at: string;
  updated_at: string;
};
export type BannerAsset = { id: string; status: string; scan_status: string };
export const bannerGateway = {
  listBanners(query: string, signal?: AbortSignal) {
    return httpClient.get<PageResult<Banner>>(`/admin/banners?${query}`, signal);
  },
  moveBanner(b: Banner, direction: "up" | "down") {
    return httpClient.post<void>(
      `/admin/banners/${encodeURIComponent(b.id)}/move`,
      { direction, version: b.version },
    );
  },
  getBanner(id: string, signal?: AbortSignal) {
    return httpClient.get<Banner>(`/admin/banners/${encodeURIComponent(id)}`, signal);
  },
  saveBanner(id: string | undefined, input: BannerInput) {
    return id
      ? httpClient.put<Banner>(
          `/admin/banners/${encodeURIComponent(id)}`,
          input,
        )
      : httpClient.post<Banner>("/admin/banners", input);
  },
  setBannerStatus(b: Banner, status: "enabled" | "disabled", reason?: string) {
    return httpClient.request<Banner>(
      `/admin/banners/${encodeURIComponent(b.id)}/status`,
      {
        method: "PATCH",
        body: JSON.stringify({ status, reason, version: b.version }),
      },
    );
  },
  deleteBanner(b: Banner) {
    return httpClient.request<void>(
      `/admin/banners/${encodeURIComponent(b.id)}`,
      { method: "DELETE", body: JSON.stringify({ version: b.version }) },
    );
  },
  bannerCourses(keyword: string, signal?: AbortSignal) {
    return httpClient.get<PageResult<{ public_id: string; title: string }>>(
      `/courses?keyword=${encodeURIComponent(keyword)}&page_size=50`, signal
    );
  },
  bannerCourse(id: string, signal?: AbortSignal) {
    return httpClient.get<{ public_id: string; title: string }>(
      `/courses/${encodeURIComponent(id)}`, signal
    );
  },
  bannerCategories(signal?: AbortSignal) {
    return httpClient.get<{ id: number; slug: string; name: string }[]>(
      "/course-categories", signal
    );
  },
  bannerImageAccess(id: string, signal?: AbortSignal) {
    return httpClient.post<{ preview_url: string }>(
      `/admin/banner-images/${encodeURIComponent(id)}/access`, undefined, signal
    );
  },
  bannerImageStatus(id: string, signal?: AbortSignal) {
    return httpClient.get<BannerAsset>(
      `/admin/banner-images/${encodeURIComponent(id)}`, signal
    );
  },
  async uploadBannerImage(file: File) {
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    )
      throw new Error("请选择 5MB 以内的 JPG、PNG 或 WebP 图片");
    return uploads.upload(file, "banner_image").catch(error=>{throw new Error(uploadErrorMessage(error))});
  },
};
